import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import ListingEditor from "../components/ListingEditor";
import StatsPanel from "../components/StatsPanel";
import { Clock, CheckCircle2, XCircle, ExternalLink, Inbox, Mail } from "lucide-react";

const STATUS = {
  pending: { label: "Live in the directory", cls: "bg-green-50 text-green-800 border-green-200", Icon: Clock, d: "Your listing is public." },
  approved: { label: "Live in the directory", cls: "bg-green-50 text-green-800 border-green-200", Icon: CheckCircle2, d: "Your listing is public. Changes go live immediately." },
  rejected: { label: "Taken down", cls: "bg-red-50 text-red-800 border-red-200", Icon: XCircle, d: "Our team has taken your listing down. Contact support@tradeflowwork.com if you think this is a mistake." },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [listing, setListing] = useState(undefined);
  const [enquiries, setEnquiries] = useState([]);

  useEffect(() => {
    if (!user || user.role === "admin") return;
    api.get("/me/listing").then((r) => setListing(r.data)).catch(() => setListing(null));
    api.get("/me/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  }, [user]);

  if (!user) return null;
  if (user.role === "admin") return <Navigate to="/admin/listings" replace />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="dashboard-page">
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Business dashboard</p>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Hello, {user.name?.split(" ")[0] || "there"}.</h1>

      {listing && <StatusBanner listing={listing} />}
      {listing && <StatsPanel />}

      <h2 className="font-display text-2xl font-bold mt-10 mb-4">{listing ? "Your listing" : "Create your listing"}</h2>
      {listing !== undefined && <ListingEditor listing={listing} user={user} onSaved={setListing} />}

      <h2 className="font-display text-2xl font-bold mt-12 mb-4">Enquiries</h2>
      <EnquiryList rows={enquiries} />
    </div>
  );
}

function StatusBanner({ listing }) {
  const s = STATUS[listing.status] || STATUS.pending;
  return (
    <div className={`mt-6 p-4 rounded-lg border flex items-start justify-between gap-4 flex-wrap ${s.cls}`} data-testid="listing-status">
      <div className="flex gap-3">
        <s.Icon className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold" data-testid="listing-status-label">{s.label}</p>
          <p className="text-sm">{s.d}</p>
          {listing.admin_note && <p className="text-sm mt-1"><strong>Note from our team:</strong> {listing.admin_note}</p>}
        </div>
      </div>
      <div className="flex gap-4 flex-wrap">
        <Link to={`/b/${listing.id}`} className="text-sm font-medium underline inline-flex items-center gap-1" data-testid="view-listing-link">
          View listing <ExternalLink className="w-3.5 h-3.5" />
        </Link>
        {listing.status === "approved" && (
          <Link to="/boost" className="text-sm font-semibold px-3 py-1 rounded-md bg-[#FFCC00] text-black" data-testid="dashboard-boost-link">Boost with an offer</Link>
        )}
      </div>
    </div>
  );
}

function EnquiryList({ rows }) {
  if (!rows.length) {
    return (
      <div className="border-2 border-dashed border-zinc-200 rounded-lg p-10 text-center" data-testid="enquiries-empty">
        <Inbox className="w-8 h-8 mx-auto text-zinc-300 mb-2" />
        <p className="text-sm text-zinc-600">No enquiries yet. They'll appear here and arrive by email.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="enquiries-list">
      {rows.map((e) => (
        <div key={e.id} className="bg-white border border-zinc-200 rounded-lg p-5" data-testid={`enquiry-${e.id}`}>
          <div className="flex justify-between gap-4 flex-wrap">
            <p className="font-semibold">{e.name}</p>
            <p className="text-xs text-zinc-400">{new Date(e.created_at).toLocaleString("en-GB")}</p>
          </div>
          <p className="text-sm text-zinc-700 whitespace-pre-wrap mt-2">{e.message}</p>
          <div className="flex gap-4 mt-3 text-sm">
            <a href={`mailto:${e.email}`} className="underline inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {e.email}</a>
            {e.phone && <a href={`tel:${e.phone}`} className="underline">{e.phone}</a>}
          </div>
        </div>
      ))}
    </div>
  );
}
